// Canonical list of preset labels offered when marking a session as no-show.
// Imported by:
//   • Dashboard `AttendanceConfirmModal` — confirm popup on the live NowCard
//     and the action-required tray
//   • `AttendanceDetailPanel` — the Attendance page's per-session editor
//   • `WeeklyAttendance` — inline reason picker on the grid
// so the dropdown contents stay consistent across surfaces.
export const NO_SHOW_REASON_LABELS = [
  'Sick',
  'Holiday',
  'Family',
  'Appointment',
  'Transport',
  'Unauthorised',
  'Other',
] as const;

export type NoShowReasonLabel = (typeof NO_SHOW_REASON_LABELS)[number];

// Strip the longest matching preset label off the start of a stored
// no-show comment and split it into preset + freetext. Tolerates the
// historical separators (": " from the Attendance page, " — " / " - "
// from the dashboard) so legacy data still parses cleanly.
export function parseNoShowComment(
  comment: string | null,
): { preset: string; freeText: string } {
  if (!comment) return { preset: NO_SHOW_REASON_LABELS[0], freeText: '' };
  for (const label of NO_SHOW_REASON_LABELS) {
    if (comment.toLowerCase().startsWith(label.toLowerCase())) {
      const rest = comment.slice(label.length).replace(/^[:\s\-–—]+/, '');
      return { preset: label, freeText: rest };
    }
  }
  return { preset: 'Other', freeText: comment };
}

// Compose a no-show comment for storage. Single canonical format used by
// every new write so future data is uniform; legacy formats still read.
export function composeNoShowComment(preset: string, freeText: string): string {
  const free = freeText.trim();
  return free ? `${preset}: ${free}` : preset;
}
